import {
  Alert,
  Checkbox,
  Box,
  Title,
  Input,
  Text,
  Icon,
  Tooltip,
} from '@nimbus-ds/components';
import { FormField } from '@nimbus-ds/patterns';
import {
  InterfaceNameChecked,
  InterfaceNameValue,
  BaseCard,
} from '@tiendanube/components';
import { QuestionCircleIcon } from '@tiendanube/icons';
import { InputNumberNimbus, Stack, useResponsive } from 'commons/components';
import { useGetCurrency, useHasPermission } from 'domains/Auth/hooks';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import ApplyValuesToAllVariants from '../ApplyValuesToAllVariants';
import type { OnChangeToAllVariants } from '../Variants/types';

export enum NamesEnum {
  PRICE = 'price',
  PROMOTIONAL_PRICE = 'promotionalPrice',
  PUBLISHED = 'pricePublished',
  COST = 'costPrice',
}

export type PricesNamesTypes =
  | NamesEnum.PRICE
  | NamesEnum.PROMOTIONAL_PRICE
  | NamesEnum.PUBLISHED
  | NamesEnum.COST;
export type ValuePricesTypes = string | boolean;
interface onChangeProps {
  name: PricesNamesTypes;
  value: ValuePricesTypes;
}

interface PricesProps {
  price: string;
  promotionalPrice: string;
  published: boolean;
  costPrice: string;
  onChange: (data: onChangeProps) => void;
  hasVariants?: boolean;
  onChangeToAllVariants?: OnChangeToAllVariants;
}

function Prices({
  price,
  promotionalPrice,
  published,
  costPrice,
  onChange,
  onChangeToAllVariants,
}: PricesProps): JSX.Element {
  const t = useTranslationCatalog();

  const { symbol } = useGetCurrency();

  const { isMobile } = useResponsive();

  const canEditPrice = useHasPermission('edit_price');

  const handleChange = (data: InterfaceNameValue) => {
    onChange(data as onChangeProps);
  };

  const handlePublished = (value: InterfaceNameChecked) => {
    onChange({ name: NamesEnum.PUBLISHED, value: value.checked });
  };

  const hasPromotionalAndCost = promotionalPrice !== '' && costPrice !== '';
  const hasPriceAndCost = price !== '' && costPrice !== '';

  const costMarginForPromotional = hasPromotionalAndCost
    ? Math.round(
        ((Number(promotionalPrice) - Number(costPrice)) /
          Number(promotionalPrice)) *
          100,
      )
    : '--';

  const costMarginForPrice = hasPriceAndCost
    ? Math.round(((Number(price) - Number(costPrice)) / Number(price)) * 100)
    : '--';

  const margin = promotionalPrice
    ? costMarginForPromotional
    : costMarginForPrice;

  const marginValue = margin === '--' ? '--' : `${margin} %`;
  const positionTooltip = isMobile ? 'left' : 'bottom';

  return (
    <BaseCard>
      <BaseCard.Header>
        <Title as="h4">{`${t('products.detail.prices')}`}</Title>
      </BaseCard.Header>
      <BaseCard.Body>
        <Stack column align="stretch" spacing="loose">
          <Stack column align="stretch">
            <Stack align="flex-start">
              <Stack.Item fill>
                <FormField label={t('products.detail.price')} id="price">
                  <InputNumberNimbus
                    type="float"
                    aria-label={t('products.detail.price')}
                    title={t('products.detail.price')}
                    append={symbol}
                    appendPosition="start"
                    value={price}
                    placeholder="0.00"
                    name={NamesEnum.PRICE}
                    disabled={!canEditPrice || !published}
                    onChange={(e) =>
                      handleChange({
                        name: NamesEnum.PRICE,
                        value: e.target.value,
                      })
                    }
                  />
                </FormField>
                {onChangeToAllVariants && (
                  <ApplyValuesToAllVariants
                    value={price}
                    onChange={onChangeToAllVariants('price', price)}
                  />
                )}
              </Stack.Item>
              <Stack.Item fill>
                <FormField
                  label={t('products.detail.promotionalPrice')}
                  id="promotionalPrice"
                >
                  <InputNumberNimbus
                    type="float"
                    aria-label={t('products.detail.promotionalPrice')}
                    title={t('products.detail.promotionalPrice')}
                    append={symbol}
                    appendPosition="start"
                    value={promotionalPrice}
                    placeholder="0.00"
                    name={NamesEnum.PROMOTIONAL_PRICE}
                    disabled={!canEditPrice || !published}
                    onChange={(evt) =>
                      handleChange({
                        name: NamesEnum.PROMOTIONAL_PRICE,
                        value: evt.target.value,
                      })
                    }
                  />
                </FormField>
                {onChangeToAllVariants && (
                  <ApplyValuesToAllVariants
                    value={promotionalPrice}
                    onChange={onChangeToAllVariants(
                      'promotionalPrice',
                      promotionalPrice,
                    )}
                  />
                )}
              </Stack.Item>
            </Stack>
            <Checkbox
              label={t('products.detail.showPrice')}
              name={NamesEnum.PUBLISHED}
              onChange={(e) => handlePublished(e.target)}
              checked={published}
            />

            {!published && (
              <Box padding="0-5">
                <Alert appearance="primary">
                  {t('products.detail.alertWithOutPrice')}
                </Alert>
              </Box>
            )}
          </Stack>
          <Stack column gap="1" align="stretch">
            <Box display="grid" gridTemplateColumns="1fr 1fr" gridGap="4">
              <Stack.Item fill>
                <FormField label={t('products.detail.cost')} id="cost">
                  <InputNumberNimbus
                    type="float"
                    aria-label={t('products.detail.cost')}
                    title={t('products.detail.cost')}
                    append={symbol}
                    appendPosition="start"
                    value={costPrice}
                    placeholder="0.00"
                    name={NamesEnum.COST}
                    disabled={!canEditPrice || !published}
                    onChange={(evt) =>
                      handleChange({
                        name: NamesEnum.COST,
                        value: evt.target.value,
                      })
                    }
                  />
                </FormField>
                {onChangeToAllVariants && (
                  <ApplyValuesToAllVariants
                    value={costPrice}
                    onChange={onChangeToAllVariants('costPrice', costPrice)}
                  />
                )}
              </Stack.Item>
              <Stack column align="flex-start" gap="1">
                <Stack gap="0-5">
                  <Text fontSize="caption">
                    {t('products.detail.labelMargin')}
                  </Text>
                  {hasPromotionalAndCost && (
                    <Tooltip
                      content={t('products.detail.infoMargin')}
                      position={positionTooltip}
                    >
                      <Icon source={<QuestionCircleIcon size="small" />} />
                    </Tooltip>
                  )}
                </Stack>
                <Input value={marginValue} name="margen" disabled />
              </Stack>
            </Box>
            <Box display="grid" gridTemplateColumns="1fr">
              <Text fontSize="caption">{t('products.detail.costInfo')}</Text>
            </Box>
          </Stack>
        </Stack>
      </BaseCard.Body>
    </BaseCard>
  );
}

export default Prices;
